import type { PiiProcessingState, PiiProcessingStepStatus } from "@repo/common";

export type PiiRedactionPresentationKind =
	| "complete"
	| "processing"
	| "review"
	| "partial"
	| "error"
	| "not_redacted";

export interface PiiRedactionPresentation {
	kind: PiiRedactionPresentationKind;
	label: string;
	description: string;
	canRunRedaction: boolean;
	canApproveReview: boolean;
	actionLabel:
		| "Redact PII"
		| "Retry PII Redaction"
		| "Resume PII Redaction"
		| "Redaction In Progress";
}

function alignmentDescription(state: PiiProcessingState): string {
	const alignment = state.alignment;
	if (!alignment || alignment.methodsUsed.length === 0) return "";
	const methods = alignment.methodsUsed
		.map((method) =>
			method === "llm" ? "LLM" : `${method[0].toUpperCase()}${method.slice(1)}`,
		)
		.join(" → ");
	const confidence =
		typeof alignment.finalConfidence === "number"
			? ` at ${Math.round(alignment.finalConfidence * 100)}% confidence`
			: "";
	return ` Alignment used ${methods}${confidence}.`;
}

function completedLabel(state: PiiProcessingState): string {
	if (state.humanReview?.status === "approved") {
		return "PII Redacted · Human Reviewed";
	}
	const alignment = state.alignment;
	if (!alignment?.finalMethod) return "PII Redacted";
	const method =
		alignment.finalMethod === "llm"
			? "LLM-assisted"
			: alignment.finalMethod === "contextual"
				? "Contextual-assisted"
				: "Canonical";
	const confidence =
		typeof alignment.finalConfidence === "number"
			? ` · ${Math.round(alignment.finalConfidence * 100)}%`
			: "";
	return `PII Redacted · ${method}${confidence}`;
}

type PiiCallState = {
	isPiiRedacted?: boolean;
	piiProcessing?: PiiProcessingState | null;
};

const SUCCESS_STATUSES = new Set<PiiProcessingStepStatus>([
	"completed",
	"not_required",
]);

const STEP_LABELS: Record<
	keyof Pick<
		PiiProcessingState,
		"detection" | "text" | "audio" | "transcriptArtifacts"
	>,
	string
> = {
	detection: "PII detection",
	text: "text redaction",
	audio: "audio redaction",
	transcriptArtifacts: "transcript cleanup",
};

function stepNamesWithStatus(
	state: PiiProcessingState,
	status: PiiProcessingStepStatus,
): string[] {
	return (Object.keys(STEP_LABELS) as Array<keyof typeof STEP_LABELS>)
		.filter((step) => state[step] === status)
		.map((step) => STEP_LABELS[step]);
}

function joinStepNames(steps: string[]): string {
	if (steps.length <= 1) return steps[0] ?? "PII processing";
	if (steps.length === 2) return `${steps[0]} and ${steps[1]}`;
	return `${steps.slice(0, -1).join(", ")}, and ${steps.at(-1)}`;
}

export function getPiiRedactionPresentation(
	call: PiiCallState,
): PiiRedactionPresentation {
	const state = call.piiProcessing;
	if (state?.correctionOpen) {
		return {
			kind: "review",
			label: "PII Review Required",
			description:
				"An administrator reopened this call for correction. Customer access is paused until the reviewed result is published.",
			canRunRedaction: false,
			canApproveReview: false,
			actionLabel: "Redact PII",
		};
	}

	// Older call documents only have the aggregate marker.
	if (!state) {
		if (call.isPiiRedacted) {
			return {
				kind: "complete",
				label: "PII Redacted",
				description:
					"PII redaction completed. Detailed component status is unavailable for this older call.",
				canRunRedaction: false,
				canApproveReview: false,
				actionLabel: "Redact PII",
			};
		}
		return {
			kind: "not_redacted",
			label: "PII Not Redacted",
			description: "PII redaction has not been run for this call.",
			canRunRedaction: true,
			canApproveReview: false,
			actionLabel: "Redact PII",
		};
	}

	// The aggregate marker is written only after all required cleanup succeeds.
	// Prefer it over stale component metadata left by older promotion code.
	const isComplete =
		call.isPiiRedacted === true ||
		(state.detection === "completed" &&
			SUCCESS_STATUSES.has(state.text) &&
			SUCCESS_STATUSES.has(state.audio) &&
			SUCCESS_STATUSES.has(state.transcriptArtifacts));
	if (isComplete) {
		const entityDescription =
			state.entitiesCount > 0
				? `${state.entitiesCount} PII ${state.entitiesCount === 1 ? "entity was" : "entities were"} handled.`
				: "No redactable PII was detected.";
		return {
			kind: "complete",
			label: completedLabel(state),
			description: `PII processing completed. ${entityDescription}${alignmentDescription(state)}`,
			canRunRedaction: false,
			canApproveReview: false,
			actionLabel: "Redact PII",
		};
	}
	if (
		state.humanReview?.status === "rejected" &&
		state.humanReview.attemptId === state.attemptId
	) {
		return {
			kind: "review",
			label: "Manual Resolution Required",
			description:
				"A human reviewer declined this redaction. The call remains hidden from customers until the privacy issue is resolved.",
			canRunRedaction: true,
			canApproveReview: false,
			actionLabel: "Retry PII Redaction",
		};
	}

	const failedSteps = stepNamesWithStatus(state, "failed");
	if (failedSteps.length > 0 || state.failureReason) {
		return {
			kind: "error",
			label: "PII Redaction Error",
			description: `PII processing failed during ${joinStepNames(failedSteps)}. Some content may already be redacted; retry to complete every required step.`,
			canRunRedaction: true,
			canApproveReview: false,
			actionLabel: "Retry PII Redaction",
		};
	}

	if (state.inProgress) {
		return {
			kind: "processing",
			label: "PII Redaction In Progress",
			description:
				"PII redaction is running in the background. This status will update automatically when processing finishes.",
			canRunRedaction: false,
			canApproveReview: false,
			actionLabel: "Redaction In Progress",
		};
	}

	if (
		state.humanReview?.status !== "approved" &&
		(state.requiresHumanReview ||
			state.alignment?.requiresHumanReview ||
			state.alignment?.status === "requires_human_review")
	) {
		const alignment = state.alignment;
		const coverage = alignment
			? ` ${alignment.resolvedOccurrences}/${alignment.expectedOccurrences} occurrence checks were resolved.`
			: "";
		return {
			kind: "review",
			label: "PII Review Required",
			description: `Available PII matches were redacted, but automatic certification could not be completed.${coverage}${alignmentDescription(state)}`,
			canRunRedaction: true,
			canApproveReview: true,
			actionLabel: "Resume PII Redaction",
		};
	}

	const statuses = [
		state.detection,
		state.text,
		state.audio,
		state.transcriptArtifacts,
	];
	if (statuses.every((status) => status === "disabled")) {
		return {
			kind: "not_redacted",
			label: "PII Not Redacted",
			description: "PII redaction was disabled when this call was processed.",
			canRunRedaction: true,
			canApproveReview: false,
			actionLabel: "Redact PII",
		};
	}

	const pendingSteps = stepNamesWithStatus(state, "pending");
	return {
		kind: "partial",
		label: "PII Partially Redacted",
		description:
			pendingSteps.length > 0
				? `PII processing is partially complete and is waiting for ${joinStepNames(pendingSteps)}.`
				: "Some PII processing steps completed, but the call is not fully certified as redacted.",
		canRunRedaction: true,
		canApproveReview: false,
		actionLabel: "Resume PII Redaction",
	};
}

export type PiiSidecarPresentation = {
	status: "purged" | "not_created" | "stored" | "not_available";
	badgeLabel: string;
	description: string;
	canView: boolean;
	canPurge: boolean;
};

export function getPiiSidecarPresentation(
	call: PiiCallState & {
		gcsUri?: string | null;
		isPiiSidecarPurged?: boolean;
	},
): PiiSidecarPresentation {
	if (call.isPiiSidecarPurged) {
		return {
			status: "purged",
			badgeLabel: "Purged",
			description:
				"The companion JSON detection file has been purged from Cloud Storage.",
			canView: false,
			canPurge: false,
		};
	}

	const state = call.piiProcessing;
	if (state?.detection === "completed" && state.entitiesCount === 0) {
		return {
			status: "not_created",
			badgeLabel: "No PII Detected",
			description:
				"No PII was detected, so no companion JSON detection file was created in Cloud Storage.",
			canView: false,
			canPurge: false,
		};
	}

	if (state?.inProgress) {
		return {
			status: "not_available",
			badgeLabel: "In Progress",
			description: "PII detection is currently in progress.",
			canView: false,
			canPurge: false,
		};
	}

	if (!call.gcsUri) {
		return {
			status: "not_available",
			badgeLabel: "No Audio",
			description: "Call does not have an archived recording in Cloud Storage.",
			canView: false,
			canPurge: false,
		};
	}

	return {
		status: "stored",
		badgeLabel: "Stored in GCP",
		description: "Companion JSON detection file is stored in Cloud Storage.",
		canView: true,
		canPurge: call.isPiiRedacted === true,
	};
}
